import React from 'react';

import { Icons } from '@shared/components/ui';

/**
 * The cross-area payoff — the actual differentiator, and the reason breadth is a
 * feature rather than scope creep.
 *
 * Every example below maps to a shipped correlation rule in
 * `pa.core.ambient.patterns.rule_sets._cross_domain_rules`. Keep it that way: the
 * whole point of this section is that it is not aspirational.
 */
const CORRELATIONS = [
  {
    areas: 'Mail × Calendar',
    insight:
      'An email arrives from someone you are meeting in an hour. Conway flags it before you walk in, because right now it is the most important thing in your inbox.',
  },
  {
    areas: 'Work × People',
    insight:
      'The same project has surfaced in eleven emails and four meetings in two days. That is a surge worth naming while it is still happening.',
  },
];

const CrossAreaSection: React.FC = () => {
  return (
    <section id="cross-area" className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center sm:mb-16">
          <div className="bg-muted text-muted-foreground mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full">
            <Icons.Sparkles size="lg" />
          </div>
          <h2 className="text-foreground text-3xl font-light sm:text-4xl">
            Then the Areas <strong className="font-semibold">Compare Notes</strong>
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
            This is the part no single-purpose app can reach. Every area publishes what happens in
            it to a shared stream, and Conway watches for the places where two areas explain each
            other.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {CORRELATIONS.map(item => (
            <div key={item.areas} className="bg-card border-border rounded-sm border p-6">
              <div className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                {item.areas}
              </div>
              <p className="text-foreground text-sm leading-relaxed">{item.insight}</p>
            </div>
          ))}
        </div>
        <div className="border-border mx-auto mt-12 max-w-3xl border-t pt-8 text-center">
          <p className="text-muted-foreground text-lg leading-relaxed">
            Every Sunday it runs the wide version — calendar, health, money, reading, and email in
            one pass — and sends you what it found.
          </p>
          <p className="text-foreground mt-6 text-lg leading-relaxed">
            This is why breadth is the feature. Adding an area is never really about the area:{' '}
            <strong className="font-semibold">
              every new one gives all the others something more to be compared against
            </strong>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default CrossAreaSection;
